import { Car } from 'lucide-react';

import { cn } from '../utils';

/**
 * Three sizes of one lot photo: `sm` is the bids-table cell, `md` the client
 * page's «Ставки» rows (owner, 2026-10-11: those rows were too short to read),
 * `lg` the hero next to the car title on the bid detail page.
 */
const SIZES = {
  sm: { box: 'h-8 w-11 rounded-cf-sm', icon: 14 },
  md: { box: 'h-14 w-20 rounded-cf-md', icon: 20 },
  lg: { box: 'h-[72px] w-24 rounded-cf-md lg:h-[90px] lg:w-[120px]', icon: 28 },
} as const;

export type LotThumbnailSize = keyof typeof SIZES;

/**
 * Fixed-size lot photo, or a neutral placeholder when there is none.
 *
 * `src` is a URL the API has ALREADY resolved (`lotCardImageUrl` in
 * @carsfinders/contracts: the first gallery key prefixed with the CDN, or an
 * absolute URL from another source) — the admin never sees a raw CDN key and
 * never needs the CDN base. `GET /bids` sends it as `carThumbnailUrl`,
 * `GET /bids/:id` as `carImageUrl`; both are null for a lot with no photo.
 */
export function LotThumbnail({
  src,
  size = 'sm',
  alt = '',
}: {
  src?: string | null;
  size?: LotThumbnailSize;
  alt?: string;
}) {
  const { box, icon } = SIZES[size];
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        data-testid="lot-thumbnail"
        className={cn('shrink-0 border border-cf-line object-cover', box)}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      data-testid="lot-thumbnail-placeholder"
      className={cn(
        'flex shrink-0 items-center justify-center border border-cf-line bg-cf-fill text-cf-ink-faint',
        box,
      )}
    >
      <Car size={icon} strokeWidth={1.75} />
    </span>
  );
}
